import _ from 'lodash';

import { isEmpty, isNilOrEmpty } from '../../../../../utils/ramdaUtils';
import {
  DATASOURCE_FROM_RESOLVER,
  DATASOURCE_TYPE_SMARTSOLVEDATA,
  ERROR_LABEL_DATASOURCE_KEY,
} from '../../../../constants/editor.constants';

export const dataSourceNew = {
  formLastModified: '',
  changeType: 'ADDED',
  dataSourceKey: '',
  dataSourceProperties: {
    containerFormName: '',
    cacheData: false,
    caseInsensitiveSearch: false,
    dataKeyField: '',
    dataSourceName: '',
    pageNumber: 1,
    pageSize: 0,
    columnList: [],
    dataSourceType: DATASOURCE_TYPE_SMARTSOLVEDATA,
    dataSourceTypeStr: 'SMARTSOLVEDATA',
    filter: { Conditions: [] },
  },
};

export const getDatasourceModalTitle = ({ dataSourceEditAction, propValue }) => {
  const newTitle = 'Data Source Editor';
  if (dataSourceEditAction === 'new') {
    return `${newTitle} - (Adding Datasource)`;
  } else if (dataSourceEditAction === 'edit') {
    return `${newTitle} - (Editing ${propValue})`;
  } else if (dataSourceEditAction === 'delete') {
    return `${newTitle} - (${propValue} will be deleted after click on save)`;
  }
  return newTitle;
};

export const validateDatasourceName = ({ dataSourceKey, dataSources, dataSourceEditAction }) => {
  if (!dataSourceKey?.length) {
    return { isValid: false };
  }

  const isNameUnique = !Object.keys(dataSources).some(
    (datasourceName) => datasourceName === dataSourceKey
  );

  if (dataSourceEditAction === 'new' && !isNameUnique) {
    return {
      isValid: false,
      [ERROR_LABEL_DATASOURCE_KEY]: 'Data Source Name should be unique',
    };
  }

  return { isValid: true };
};

const isOrderByValid = (orderBy) => {
  if (isNilOrEmpty(orderBy)) {
    return true;
  }

  return !orderBy.some((row) => isEmpty(row.direction) || isEmpty(row.field));
};

export const validateDatasource = ({
  dataSourceObject,
  dataSources,
  dataSourceEditAction,
  selectedFields,
}) => {
  const { dataSourceKey, dataSourceProperties } = dataSourceObject;

  if (!dataSourceKey || !dataSourceProperties || !selectedFields?.length) {
    return false;
  }

  const validationDetails = validateDatasourceName({
    dataSourceKey,
    dataSources,
    dataSourceEditAction,
  });

  return validationDetails.isValid && isOrderByValid(dataSourceProperties.orderBy);
};

export const prepareDataSourceToSave = (dataSourceObject = {}, dataSourceKey) => {
  let cleanDataSource = Object.assign({}, dataSourceObject);
  cleanDataSource = {
    ...cleanDataSource,
    dataSourceKey: dataSourceKey || cleanDataSource?.dataSourceProperties?.dataSourceKey,
    dataSourceProperties: {
      ...cleanDataSource?.dataSourceProperties,
      dataSourceName:
        cleanDataSource?.dataSourceProperties?.dataSourceName ||
        cleanDataSource?.dataSourceProperties?.dataObjectValue,
    },
  };

  delete cleanDataSource?.dataSourceProperties?.dataObjectList;
  delete cleanDataSource?.dataSourceProperties?.dataObjectValue;
  if (
    cleanDataSource?.dataSourceProperties?.orderBy &&
    cleanDataSource?.dataSourceProperties?.orderBy?.length === 0
  ) {
    delete cleanDataSource?.dataSourceProperties?.orderBy;
  }

  if (cleanDataSource?.dataSourceProperties?.dataKeyField?.trim()?.length === 0) {
    delete cleanDataSource?.dataSourceProperties?.dataKeyField;
  }
  return cleanDataSource;
};

export const isResolver = (datasource) => datasource?.dataSourceFrom === DATASOURCE_FROM_RESOLVER;

const getTranslatedFields = ({ fields, entityTranslations }) => {
  const translatedFields = Object.entries(fields).reduce((fields, [fieldTitle, fieldDetails]) => {
    if (['WARNINGS', 'ERRORS'].includes(fieldTitle)) {
      return fields;
    }

    const field = {
      label: entityTranslations[fieldDetails.properties.Caption],
      value: fieldTitle,
    };
    fields.push(field);

    return fields;
  }, []);

  return translatedFields.sort((a, b) => a.label?.localeCompare(b.label));
};

const getSortedDataObjectList = ({ parentTitle, entityDefinition, entityTranslations }) => {
  const dataObjectList = Object.keys(entityDefinition);
  const translatedDataObjectList = dataObjectList.map((dataObjectTitle) => {
    return {
      translatedTitle: entityTranslations[dataObjectTitle.replace(`${parentTitle}-`, '')],
      dataObjectTitle,
    };
  });

  return translatedDataObjectList.sort((a, b) =>
    a.translatedTitle?.localeCompare(b.translatedTitle)
  );
};

export const getFormDataObjectList = ({
  entityDefinition,
  entityTranslations,
  parentTitle,
  depth = 0,
}) => {
  const sortedDataObjectList = getSortedDataObjectList({
    parentTitle,
    entityDefinition,
    entityTranslations,
  });

  parentTitle && depth++;

  return sortedDataObjectList.reduce((dataObjectList, item) => {
    const dataObject = entityDefinition[item.dataObjectTitle];
    const dataObjectTitle = parentTitle
      ? item.dataObjectTitle.replace(`${parentTitle}-`, '')
      : item.dataObjectTitle;

    const prefix = depth ? `${'-'.repeat(depth)} ` : '';
    const label = entityTranslations[dataObjectTitle] ?? dataObjectTitle;

    const modifiedDataObject = {
      value: item.dataObjectTitle,
      label: `${prefix}${label}`,
      fields: getTranslatedFields({ fields: dataObject.columns, entityTranslations }),
    };

    dataObjectList.push(modifiedDataObject);

    const childrenList = getFormDataObjectList({
      entityDefinition: _.omit(dataObject, ['columns', 'properties']),
      parentTitle: item.dataObjectTitle,
      entityTranslations,
      depth,
    });

    return [...dataObjectList, ...childrenList];
  }, []);
};
